import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { splitLangPath, localizedPath } from '../../i18n/routing';

function FlagBR({ size = 14 }) {
  return (
    <svg width={size} height={size * 0.7} viewBox="0 0 20 14" aria-hidden="true" style={{ display: 'block', borderRadius: 1.5 }}>
      <rect width="20" height="14" fill="#009c3b" />
      <path d="M10 2 L18 7 L10 12 L2 7 Z" fill="#ffdf00" />
      <circle cx="10" cy="7" r="2.4" fill="#002776" />
      <path d="M7.8 6.6 Q10 5.6 12.2 6.6" stroke="#fff" strokeWidth="0.45" fill="none" />
    </svg>
  );
}
function FlagUS({ size = 14 }) {
  return (
    <svg width={size} height={size * 0.7} viewBox="0 0 20 14" aria-hidden="true" style={{ display: 'block', borderRadius: 1.5 }}>
      <rect width="20" height="14" fill="#b22234" />
      {[1, 3, 5, 7, 9, 11].map((y) => <rect key={y} y={y} width="20" height="1" fill="#fff" />)}
      <rect width="8" height="7" fill="#3c3b6e" />
      {Array.from({ length: 12 }).map((_, i) => {
        const c = i % 4, r = Math.floor(i / 4);
        return <circle key={i} cx={1.2 + c * 1.8} cy={1.2 + r * 2} r="0.35" fill="#fff" />;
      })}
    </svg>
  );
}
function FlagES({ size = 14 }) {
  return (
    <svg width={size} height={size * 0.7} viewBox="0 0 20 14" aria-hidden="true" style={{ display: 'block', borderRadius: 1.5 }}>
      <rect width="20" height="14" fill="#c60b1e" />
      <rect y="3.5" width="20" height="7" fill="#ffc400" />
    </svg>
  );
}

const LANGS = [
  { code: 'pt', label: 'PT-BR', Flag: FlagBR },
  { code: 'en', label: 'EN',    Flag: FlagUS },
  { code: 'es', label: 'ES',    Flag: FlagES },
];

export default function LangSwitch() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const lang = (i18n.resolvedLanguage || i18n.language || 'pt').slice(0, 2);

  useEffect(() => {
    function onDocClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const current = LANGS.find((l) => l.code === lang) || LANGS[0];
  const Cur = current.Flag;

  function choose(code) {
    // Navega em vez de só trocar o idioma no i18next: agora cada idioma
    // tem URL própria, e mudar só o estado deixaria a barra de endereço
    // mentindo — além de tornar a escolha impossível de compartilhar.
    // O LangSync em App.js aplica o idioma a partir da nova URL.
    const { rest } = splitLangPath(location.pathname);
    navigate(localizedPath(rest, code) + location.search + location.hash);
    setOpen(false);
  }

  return (
    <div ref={ref} style={{ position: 'relative' }} className="lang-switch">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={t('nav.language')}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          height: 32, padding: '0 10px',
          border: '1px solid var(--p2a-dark-border-2)',
          borderRadius: 4,
          background: 'transparent',
          color: 'var(--p2a-dark-fg-2)',
          fontFamily: 'var(--p2a-font-mono)',
          fontSize: 11,
          letterSpacing: '0.06em',
          cursor: 'pointer',
          transition: 'border-color var(--p2a-dur-base) var(--p2a-ease), color var(--p2a-dur-base) var(--p2a-ease)',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--p2a-green-500)'; e.currentTarget.style.color = 'var(--p2a-dark-fg-1)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--p2a-dark-border-2)'; e.currentTarget.style.color = 'var(--p2a-dark-fg-2)'; }}
      >
        <Cur />
        <span className="lang-switch-label">{current.label}</span>
        <svg width="8" height="8" viewBox="0 0 8 8" aria-hidden="true">
          <path d="M1 2.5 L4 5.5 L7 2.5" stroke="currentColor" strokeWidth="1" fill="none" />
        </svg>
      </button>
      <style>{`
        @media (max-width: 560px) {
          .lang-switch-label { display: none; }
        }
      `}</style>
      {open && (
        <div
          role="listbox"
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', right: 0,
            minWidth: 140,
            background: 'var(--p2a-dark-surface)',
            border: '1px solid var(--p2a-dark-border-2)',
            borderRadius: 4,
            boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
            overflow: 'hidden',
            zIndex: 200,
          }}
        >
          {LANGS.map((l) => {
            const F = l.Flag;
            const active = l.code === lang;
            return (
              <button
                key={l.code}
                role="option"
                aria-selected={active}
                onClick={() => choose(l.code)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 12px',
                  border: 'none',
                  background: active ? 'rgba(34,197,94,0.08)' : 'transparent',
                  color: active ? 'var(--p2a-green-400)' : 'var(--p2a-dark-fg-2)',
                  fontFamily: 'var(--p2a-font-mono)', fontSize: 11, letterSpacing: '0.06em',
                  textAlign: 'left', cursor: 'pointer',
                }}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
              >
                <F />
                <span>{l.label}</span>
                {active && <span style={{ marginLeft: 'auto', width: 4, height: 4, borderRadius: '50%', background: 'var(--p2a-green-500)' }} />}
              </button>
            );
          })}
          <div style={{
            padding: '8px 12px',
            borderTop: '1px solid var(--p2a-dark-border)',
            fontFamily: 'var(--p2a-font-mono)',
            fontSize: 10,
            letterSpacing: '0.06em',
            color: 'var(--p2a-dark-fg-4)',
          }}>
            {t('lang_switch.soon')}
          </div>
        </div>
      )}
    </div>
  );
}
